/**
 * TeachPanel — Zone 2 for the `teach` intent (spec §3, standards-first per
 * Michael 2026-07-16): subject chips → state pill (preselected from the
 * school context) → ONE search box over the teacher's state's real standards
 * (the prod-proven StandardPicker mechanism, re-arranged, grade-guaranteed).
 *
 * The search is HIDDEN (never disabled) until a real grade band exists —
 * grade lives in the fixed Zone-3 position; this panel only reads it.
 * Picking a standard sets the same `{id, code, stateCode, description,
 * gradeMin, gradeMax, stamp}` value the pipeline already consumes
 * (requestedStandardId — IIG planner step 1a, authoritative).
 *
 * Subject chips map to per-state standard-set spellings via THE canonical
 * pattern map (mirrors iig/lc-intent-probes.mjs — 203/204 coverage measured;
 * zero matches → the state's raw list under "More subjects…").
 */
import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import usePrintables from '../../../services/usePrintables';
import { US_STATES } from '../StatePicker';
import { chipsForJurisdiction, US_CHIPS } from './chipMaps';
import { bandToRange, ALL_GRADES } from './GradeBandPicker';

/** Some states' standard descriptions carry raw HTML (NC uses <ul><li> lists —
 * seen live 2026-07-16). Strip tags for display; the payload keeps raw text. */
const stripHtml = (s) => String(s ?? '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();

// SUBJECT_CHIPS = the US chip set. STILL EXPORTED (lockstep contract with
// iig/lc-intent-probes.mjs, studiocore-app.md §22). Non-US jurisdictions get
// their own chip maps from ./chipMaps (LatAm expansion 2026-07-27).
export const SUBJECT_CHIPS = US_CHIPS;

export default function TeachPanel({ subject, onSubjectChange, standard, onStandardChange, stateCode, onStateChange, gradeBand, onNeedState, jurisdictions, token }) {
  const { t } = useTranslation('home');
  const { fetchStateSubjects, fetchStateStandards } = usePrintables();
  const [stateSubjects, setStateSubjects] = useState([]); // the state's raw spellings
  const [moreOpen, setMoreOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [changingState, setChangingState] = useState(false);
  const debounceRef = useRef(null);

  const hasRealBand = !!gradeBand && gradeBand !== ALL_GRADES;
  // Jurisdiction list: fetched (US + territories + countries) with US fallback.
  const jList = (jurisdictions && jurisdictions.length) ? jurisdictions : US_STATES;
  const stateName = jList.find((s) => s.code === stateCode)?.name
    ?? US_STATES.find((s) => s.code === stateCode)?.name ?? stateCode;
  // Chip set follows the selected jurisdiction (BNCC/MX campos/CL/PR or US).
  const chips = useMemo(() => chipsForJurisdiction(stateCode), [stateCode]);

  // Load the state's subject spellings whenever the state changes.
  useEffect(() => {
    if (!stateCode) return;
    let cancelled = false;
    fetchStateSubjects(stateCode)
      .then((rows) => { if (!cancelled) setStateSubjects(rows.map((r) => r.subject)); })
      .catch(() => { if (!cancelled) setStateSubjects([]); });
    return () => { cancelled = true; };
  }, [stateCode, fetchStateSubjects]);

  // The chip's matching per-jurisdiction subject strings (CSV for the endpoint).
  // A chip is keyed by its payload; multiple non-US chips can share a payload
  // (e.g. MX has two Social-Studies campos), so match by the active chip object.
  const activeChip = useMemo(() => chips.find((c) => c.payload === subject), [chips, subject]);
  const matchedSubjects = useMemo(() => {
    // Raw "More subjects…" pick: subject IS the exact ss.subject spelling.
    if (subject && stateSubjects.includes(subject)) return [subject];
    if (!activeChip) return [];
    return stateSubjects.filter((s) =>
      activeChip.patterns.some((re) => re.test(s)) && !(activeChip.exclude || []).some((re) => re.test(s)));
  }, [subject, activeChip, stateSubjects]);

  // Debounced standards search — grade-scoped by construction (band required).
  useEffect(() => {
    if (!stateCode || !subject || !hasRealBand) { setResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) { setResults([]); setLoading(false); return; }
    const { gradeMin, gradeMax } = bandToRange(gradeBand);
    setLoading(true);
    setSearchError(false);
    debounceRef.current = setTimeout(() => {
      // Pipe-delimited: LatAm subject names contain commas (backend splits on '|').
      const subjectCsv = matchedSubjects.length ? matchedSubjects.join('|') : undefined;
      fetchStateStandards(stateCode, { subject: subjectCsv, gradeMin, gradeMax, q: q.trim(), limit: 10 })
        .then((d) => setResults(d?.standards ?? []))
        .catch(() => { setResults([]); setSearchError(true); })
        .finally(() => setLoading(false));
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [stateCode, subject, gradeBand, hasRealBand, q, matchedSubjects, fetchStateStandards]);

  // ── Selected standard: chip + stamp opt-in (unchanged pipeline contract) ──
  if (standard?.code) {
    const codeDisplay = String(standard.code).toUpperCase().startsWith(`${String(standard.stateCode || '').toUpperCase()}.`)
      ? standard.code
      : `${standard.stateCode} ${standard.code}`;
    return (
      <div style={{ fontFamily: 'inherit' }}>
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          fontSize: '0.8rem', fontWeight: 600, color: 'rgba(127,75,246,0.95)',
          background: 'rgba(127,75,246,0.14)', border: '1px solid rgba(127,75,246,0.35)',
          padding: '6px 10px', borderRadius: 8, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        }}>
          {t('teach.alignedTo', { code: codeDisplay })}
          <span onClick={() => onStandardChange(null)} title={t('teach.removeChip')} style={{ cursor: 'pointer', color: 'rgba(27,37,80,0.57)', fontWeight: 700 }}>×</span>
        </span>
        {standard.description && (
          <div style={{
            fontSize: '0.75rem', color: 'rgba(27,37,80,0.427)', marginTop: 6, lineHeight: 1.4,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
          }}>
            {stripHtml(standard.description)}
          </div>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: '0.78rem', color: 'rgba(27,37,80,0.617)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={standard.stamp !== false}
            onChange={(e) => onStandardChange({ ...standard, stamp: e.target.checked })}
          />
          {t('teach.stampCheckbox')}
        </label>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: 'inherit', display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* 1. Subject chips */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {chips.map((chip) => {
          const active = subject === chip.payload;
          const chipLabel = chip.labelKey ? t(chip.labelKey) : chip.label;
          return (
            <button
              key={chip.label ?? chip.labelKey}
              type="button"
              onClick={() => onSubjectChange(active ? null : chip.payload)}
              style={{
                padding: '8px 14px', borderRadius: 999, fontSize: '0.82rem', fontWeight: 600,
                border: active ? '1px solid rgba(127,75,246,0.5)' : '1px solid rgba(27,37,80,0.114)',
                background: active ? 'rgba(127,75,246,0.16)' : 'rgba(27,37,80,0.038)',
                color: active ? 'rgba(127,75,246,0.95)' : 'rgba(27,37,80,0.76)',
                cursor: 'pointer', fontFamily: 'inherit',
              }}
            >
              {chipLabel}
            </button>
          );
        })}
        {stateSubjects.length > 0 && (
          <button
            type="button"
            onClick={() => setMoreOpen((v) => !v)}
            style={{ background: 'transparent', border: 'none', color: 'rgba(27,37,80,0.427)', fontSize: '0.75rem', cursor: 'pointer', fontFamily: 'inherit' }}
          >
            {t('teach.moreSubjects')}
          </button>
        )}
      </div>
      {/* overscrollBehavior contain on all internal scrollers: reaching the
          list's edge must not scroll-chain into the page (Michael 2026-07-16). */}
      {moreOpen && (
        <div style={{ maxHeight: 150, overflowY: 'auto', overscrollBehavior: 'contain', display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {stateSubjects.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => { onSubjectChange(s); setMoreOpen(false); }}
              style={{
                padding: '4px 10px', borderRadius: 999, fontSize: '0.7rem', fontFamily: 'inherit',
                border: subject === s ? '1px solid rgba(127,75,246,0.5)' : '1px solid rgba(27,37,80,0.095)',
                background: subject === s ? 'rgba(127,75,246,0.14)' : 'transparent',
                color: subject === s ? 'rgba(127,75,246,0.95)' : 'rgba(27,37,80,0.522)', cursor: 'pointer',
              }}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* 2. State pill */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.8rem', color: 'rgba(27,37,80,0.665)' }}>
        {stateCode && !changingState ? (
          <>
            <span>📍 {stateName}</span>
            <button
              type="button"
              onClick={() => setChangingState(true)}
              style={{ background: 'transparent', border: 'none', color: 'rgba(127,75,246,0.7)', fontSize: '0.72rem', cursor: 'pointer', fontFamily: 'inherit', textDecoration: 'underline' }}
            >
              {t('teach.changeState')}
            </button>
          </>
        ) : (
          <select
            value={stateCode ?? ''}
            onChange={(e) => { if (e.target.value) { onStateChange(e.target.value); setChangingState(false); } }}
            onFocus={() => { if (!stateCode) onNeedState?.(); }}
            style={{
              padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(27,37,80,0.114)',
              background: '#FFFFFF', color: 'rgba(27,37,80,0.855)', fontSize: '0.8rem',
              fontFamily: 'inherit', cursor: 'pointer', colorScheme: 'dark',
            }}
          >
            <option value="" style={{ background: '#FFFFFF', color: '#1B2550' }}>{t('teach.yourState')}</option>
            {(() => {
              const countries = jList.filter((s) => s.jurisdictionType === 'country').sort((a, b) => a.name.localeCompare(b.name));
              const us = jList.filter((s) => s.jurisdictionType !== 'country').sort((a, b) => a.name.localeCompare(b.name));
              const opt = (s) => <option key={s.code} value={s.code} style={{ background: '#FFFFFF', color: '#1B2550' }}>{s.name}</option>;
              return (
                <>
                  {countries.length > 0 && <optgroup label={t('statePicker.groupCountries')} style={{ background: '#FFFFFF', color: '#1B2550' }}>{countries.map(opt)}</optgroup>}
                  <optgroup label={t('statePicker.groupUnitedStates')} style={{ background: '#FFFFFF', color: '#1B2550' }}>{us.map(opt)}</optgroup>
                </>
              );
            })()}
          </select>
        )}
      </div>

      {/* 3. Search — hidden (never disabled) until a real grade band exists. */}
      {subject && stateCode && !hasRealBand && (
        <div style={{ fontSize: '0.75rem', color: 'rgba(27,37,80,0.427)' }}>
          {t('teach.pickGradeFirst')}
        </div>
      )}
      {subject && stateCode && hasRealBand && (
        <>
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('teach.searchPlaceholder')}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: 10, boxSizing: 'border-box',
              border: '1px solid rgba(27,37,80,0.095)', background: '#FFFFFF',
              color: 'rgba(27,37,80,0.874)', fontSize: '0.85rem', outline: 'none', fontFamily: 'inherit',
            }}
          />
          {loading && <div style={{ fontSize: '0.75rem', color: 'rgba(27,37,80,0.332)' }}>{t('teach.searching')}</div>}
          {!loading && searchError && q.trim().length >= 2 && (
            <div style={{ fontSize: '0.75rem', color: 'rgba(127,75,246,0.85)' }}>
              {t('teach.searchError')}
            </div>
          )}
          {!loading && !searchError && q.trim().length >= 2 && results.length === 0 && (
            <div style={{ fontSize: '0.75rem', color: 'rgba(27,37,80,0.332)' }}>
              {t('teach.noExactMatch')}
            </div>
          )}
          {results.length > 0 && (
            <div style={{ maxHeight: 240, overflowY: 'auto', overscrollBehavior: 'contain', display: 'flex', flexDirection: 'column', gap: 4 }}>
              {results.map((row) => (
                <button
                  key={row.id}
                  type="button"
                  onClick={() => {
                    onStandardChange({
                      id: row.id, code: row.code, stateCode, description: row.description,
                      gradeMin: row.gradeMin ?? row.grade_min ?? null, gradeMax: row.gradeMax ?? row.grade_max ?? null,
                      stamp: true,
                    });
                    setQ('');
                    setResults([]);
                  }}
                  style={{
                    textAlign: 'left', padding: '8px 10px', borderRadius: 8,
                    border: '1px solid rgba(27,37,80,0.076)', background: '#FFFFFF',
                    cursor: 'pointer', fontFamily: 'inherit', display: 'flex', flexDirection: 'column', gap: 3,
                  }}
                >
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(127,75,246,0.95)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                    {row.code}
                  </span>
                  <span style={{
                    fontSize: '0.75rem', color: 'rgba(27,37,80,0.57)', lineHeight: 1.35,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                  }}>
                    {stripHtml(row.description)}
                  </span>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
